import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';

const DAYS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
const MONTHS_PT = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const MONTHS_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function formatDate(dateStr, lang) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  const day = d.getDate();
  const month = (lang === 'en' ? MONTHS_EN : MONTHS_PT)[d.getMonth()].slice(0, 3);
  const year = d.getFullYear();
  return `${day} ${month} ${year}`;
}

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  // Monday = 0
  let day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1;
}

function toStr(date) {
  return date.toISOString().split('T')[0];
}

export default function DateRangePicker({ startDate, endDate, onChange, lang = 'pt', minDate, inline = false }) {
  const triggerRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0, placeAbove: false });
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const minDateObj = minDate ? new Date(minDate + 'T00:00:00') : today;

  const initMonth = startDate ? new Date(startDate + 'T00:00:00') : today;
  const [viewYear, setViewYear] = useState(initMonth.getFullYear());
  const [viewMonth, setViewMonth] = useState(initMonth.getMonth());
  const [open, setOpen] = useState(false);
  const [selecting, setSelecting] = useState(null); // null | 'start'
  const [hoverDate, setHoverDate] = useState(null);

  const MONTHS = lang === 'en' ? MONTHS_EN : MONTHS_PT;

  const updatePos = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const calHeight = 340;
      const spaceAbove = rect.top - 8;
      // Always open upward (above the trigger) so it never overlaps a submit
      // button rendered below the field. Clamp to viewport top if needed.
      const placeAbove = true;
      const top = spaceAbove >= calHeight
        ? rect.top - calHeight - 4
        : Math.max(8, rect.top - calHeight - 4);
      setDropdownPos({
        top,
        left: rect.left,
        width: rect.width,
        placeAbove
      });
    }
  };

  useEffect(() => {
    if (!open) return;
    window.addEventListener('scroll', updatePos, true);
    window.addEventListener('resize', updatePos);
    return () => {
      window.removeEventListener('scroll', updatePos, true);
      window.removeEventListener('resize', updatePos);
    };
  }, [open]);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const handleDayClick = (dateStr) => {
    const clicked = new Date(dateStr + 'T00:00:00');
    if (clicked < minDateObj) return;

    if (!selecting || selecting === 'start') {
      // Select start, reset end
      onChange({ startDate: dateStr, endDate: dateStr }); // same day = 1 day
      setSelecting('end');
    } else {
      // Select end
      const start = new Date(startDate + 'T00:00:00');
      if (clicked < start) {
        // clicked before start → reset
        onChange({ startDate: dateStr, endDate: dateStr });
        setSelecting('end');
      } else {
        onChange({ startDate, endDate: dateStr });
        setSelecting(null);
        setOpen(false);
      }
    }
  };

  const isInRange = (dateStr) => {
    if (!startDate) return false;
    const d = new Date(dateStr + 'T00:00:00');
    const s = new Date(startDate + 'T00:00:00');
    const e = hoverDate && selecting === 'end'
      ? new Date(hoverDate + 'T00:00:00')
      : endDate ? new Date(endDate + 'T00:00:00') : s;
    return d > s && d < e;
  };

  const isStart = (dateStr) => dateStr === startDate;
  const isEnd = (dateStr) => {
    if (selecting === 'end' && hoverDate) return dateStr === hoverDate;
    return dateStr === endDate;
  };
  const isPast = (dateStr) => new Date(dateStr + 'T00:00:00') < minDateObj;

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);

  const label = lang === 'en' ? 'Dates' : 'Datas';
  const displayText = startDate
    ? endDate && endDate !== startDate
      ? `${formatDate(startDate, lang)} — ${formatDate(endDate, lang)}`
      : `${formatDate(startDate, lang)} (1 ${lang === 'en' ? 'day' : 'dia'})`
    : lang === 'en' ? 'Select dates' : 'Selecionar datas';

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          if (!open) updatePos();
          setOpen(o => !o);
          setSelecting('start');
        }}
        className="w-full flex items-center gap-2 px-3 py-3 border-2 border-gray-200 rounded-xl text-sm font-medium bg-white text-left focus:border-green-500 focus:outline-none transition-colors"
      >
        <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
        <span className={startDate ? 'text-gray-800' : 'text-gray-400'}>{displayText}</span>
      </button>

      {open && (inline ? (
        <div className="absolute z-50 mt-2 bg-white border border-gray-200 rounded-xl shadow-2xl p-3" style={{ width: '100%', minWidth: 280 }}>
          <div className="flex items-center justify-between mb-3">
            <button type="button" onClick={prevMonth} className="p-1 hover:bg-gray-100 rounded-full">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="font-semibold text-sm">{MONTHS[viewMonth]} {viewYear}</span>
            <button type="button" onClick={nextMonth} className="p-1 hover:bg-gray-100 rounded-full">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-7 mb-1">
            {DAYS.map(d => (
              <div key={d} className="text-center text-xs text-gray-400 font-medium py-1">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {Array.from({ length: firstDay }).map((_, i) => <div key={`empty-${i}`} />)}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const past = isPast(dateStr);
              const start = isStart(dateStr);
              const end = isEnd(dateStr);
              const inRange = isInRange(dateStr);
              let cls = 'text-center text-xs py-1.5 cursor-pointer select-none ';
              if (past) {
                cls += 'text-gray-300 cursor-not-allowed ';
              } else if (start || end) {
                cls += 'bg-blue-600 text-white rounded-full font-bold ';
              } else if (inRange) {
                cls += 'bg-blue-100 text-blue-800 ';
              } else {
                cls += 'hover:bg-gray-100 rounded-full ';
              }
              return (
                <div
                  key={dateStr}
                  className={cls}
                  onClick={() => !past && handleDayClick(dateStr)}
                  onMouseEnter={() => selecting === 'end' && !past && setHoverDate(dateStr)}
                  onMouseLeave={() => setHoverDate(null)}
                >
                  {day}
                </div>
              );
            })}
          </div>
          <p className="text-xs text-gray-400 mt-3 text-center">
            {selecting === 'end'
              ? (lang === 'en' ? 'Select return date' : 'Selecione a data de recolha')
              : (lang === 'en' ? 'Select delivery date' : 'Selecione a data de entrega')}
          </p>
        </div>
      ) : createPortal(
        <>
          <div className="fixed inset-0" style={{ zIndex: 2147483646 }} onClick={() => { setOpen(false); setSelecting(null); }} />
          <div className="bg-white border border-gray-200 rounded-xl shadow-2xl p-3" style={{ position: 'fixed', top: dropdownPos.top, left: Math.min(dropdownPos.left, window.innerWidth - Math.min(300, window.innerWidth - 16) - 8), width: Math.min(300, window.innerWidth - 16), zIndex: 2147483647, maxHeight: dropdownPos.placeAbove ? 'calc(' + (dropdownPos.top + 332) + 'px)' : 'calc(100vh - ' + dropdownPos.top + 'px - 8px)', overflowY: 'auto' }}>
            {/* Month navigation */}
            <div className="flex items-center justify-between mb-3">
              <button type="button" onClick={prevMonth} className="p-1 hover:bg-gray-100 rounded-full">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="font-semibold text-sm">{MONTHS[viewMonth]} {viewYear}</span>
              <button type="button" onClick={nextMonth} className="p-1 hover:bg-gray-100 rounded-full">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Day headers */}
            <div className="grid grid-cols-7 mb-1">
              {DAYS.map(d => (
                <div key={d} className="text-center text-xs text-gray-400 font-medium py-1">{d}</div>
              ))}
            </div>

            {/* Days grid */}
            <div className="grid grid-cols-7">
              {Array.from({ length: firstDay }).map((_, i) => <div key={`empty-${i}`} />)}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const past = isPast(dateStr);
                const start = isStart(dateStr);
                const end = isEnd(dateStr);
                const inRange = isInRange(dateStr);

                let cls = 'text-center text-xs py-1.5 cursor-pointer select-none ';
                if (past) {
                  cls += 'text-gray-300 cursor-not-allowed ';
                } else if (start || end) {
                  cls += 'bg-blue-600 text-white rounded-full font-bold ';
                } else if (inRange) {
                  cls += 'bg-blue-100 text-blue-800 ';
                } else {
                  cls += 'hover:bg-gray-100 rounded-full ';
                }

                return (
                  <div
                    key={dateStr}
                    className={cls}
                    onClick={() => !past && handleDayClick(dateStr)}
                    onMouseEnter={() => selecting === 'end' && !past && setHoverDate(dateStr)}
                    onMouseLeave={() => setHoverDate(null)}
                  >
                    {day}
                  </div>
                );
              })}
            </div>

            <p className="text-xs text-gray-400 mt-3 text-center">
              {selecting === 'end'
                ? (lang === 'en' ? 'Select return date' : 'Selecione a data de recolha')
                : (lang === 'en' ? 'Select delivery date' : 'Selecione a data de entrega')}
            </p>
          </div>
        </>,
        document.body
      ))}
    </div>
  );
}